import { getIframeTargetOrigin } from '../utils/iframe';
import { generateInstanceId } from '../utils/id';
import { RequestIframeClientImpl } from '../impl/client';
import { setRequestIframeLogLevel } from '../utils/logger';
import { Messages, ErrorCode, OriginConstant, LogLevel } from '../constants';
import { clearMessageChannelCache } from '../message/channel-cache';
import { warnUnsafeTargetOriginForWindow } from '../utils/warnings';
import { ensureClientDebugInterceptors, loadDebugModule, wrapClientMethodsForDebug } from '../utils/debug-lazy';
import { applyStrictClientSecurityDefaults } from '../utils/strict-mode';

/**
 * Create a client (for sending requests)
 * 
 * Note:
 * - MessageChannel is cached at the window level by secretKey (ensures unique message listener)
 * - Client instances are not cached, a new instance is created on each call
 * - This allows different versions of the library to coexist
 */
export function requestIframeClient(target, options) {
  var _resolved$options;
  var targetWindow = null;
  var defaultTargetOrigin = OriginConstant.ANY;
  if (target.tagName === 'IFRAME') {
    var iframe = target;
    targetWindow = iframe.contentWindow;
    defaultTargetOrigin = getIframeTargetOrigin(iframe);
    if (!targetWindow) {
      throw {
        message: Messages.IFRAME_NOT_READY,
        code: ErrorCode.IFRAME_NOT_READY
      };
    }
  } else {
    targetWindow = target;
    defaultTargetOrigin = OriginConstant.ANY;
  }
  var resolved = applyStrictClientSecurityDefaults(defaultTargetOrigin, options);
  var targetOrigin = resolved.targetOrigin;
  var resolvedOptions = (_resolved$options = resolved.options) !== null && _resolved$options !== void 0 ? _resolved$options : options;

  /**
   * P1: warn on unsafe default targetOrigin for Window targets.
   * - If targetOrigin is '*' and user did not configure allowedOrigins/validateOrigin,
   *   incoming message origin validation is effectively disabled.
   */
  warnUnsafeTargetOriginForWindow({
    isIframeTarget: target.tagName === 'IFRAME',
    targetOrigin,
    allowedOrigins: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.allowedOrigins,
    validateOrigin: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.validateOrigin
  });

  // Determine secretKey
  var secretKey = resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.secretKey;

  // Generate instance ID first (will be used by both client and server)
  var instanceId = generateInstanceId();

  // Create client instance (internally creates its core message server)
  var client = new RequestIframeClientImpl(targetWindow, targetOrigin, {
    secretKey,
    ackTimeout: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.ackTimeout,
    timeout: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.timeout,
    asyncTimeout: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.asyncTimeout,
    returnData: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.returnData,
    headers: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.headers,
    allowedOrigins: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.allowedOrigins,
    validateOrigin: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.validateOrigin,
    autoOpen: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.autoOpen,
    autoAckMaxMetaLength: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.autoAckMaxMetaLength,
    autoAckMaxIdLength: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.autoAckMaxIdLength
  }, instanceId);

  /**
   * Trace/log level:
   * - default: only warn/error will be printed (logger default)
   * - if trace enabled: raise log level and (optionally) enable detailed debug interceptors
   */
  if (resolvedOptions !== null && resolvedOptions !== void 0 && resolvedOptions.trace) {
    var level = resolvedOptions.trace === true ? LogLevel.TRACE : resolvedOptions.trace;
    setRequestIframeLogLevel(level);
    if (level === LogLevel.TRACE || level === LogLevel.INFO) {
      /**
       * Lazy-load debug hooks to keep main bundle smaller, but still ensure
       * the first request in trace mode won't miss debug interceptors.
       */
      wrapClientMethodsForDebug(client);
      // Preheat import early (best-effort)
      void loadDebugModule().catch(() => {
        /** ignore */
      });
      // Attach ASAP (best-effort)
      void ensureClientDebugInterceptors(client);
    }
  }
  return client;
}

/**
 * Clear MessageChannel cache (for testing or reset)
 * Note: This clears the shared message channel for the specified secretKey
 */
export function clearRequestIframeClientCache(secretKey) {
  // Client is not cached; this helper only clears shared MessageChannel cache.
  // If secretKey is provided, only clear channels under that secretKey.
  if (typeof secretKey === 'string') {
    clearMessageChannelCache(secretKey);
    return;
  }
  clearMessageChannelCache();
}